import classNames from 'classnames'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { v4 } from 'uuid'

import Icon from '@/components/Icon/Icon'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import { useAppSelector } from '@/store/hooks'
import { CardType, PriceMapType, PriceWithSlugType, PriceWithUrlType } from '@/types/card'
import { impactLink } from '@/utils/helpers'

import styles from './CardPrices.module.scss'

type TCGPContainerProps = {
  children: any
  saleEndTime: string
  tcgplayerSale: number
}

const TCGPContainer = ({ children, saleEndTime, tcgplayerSale }: TCGPContainerProps) =>
  tcgplayerSale > 0 ? (
    <OverlayTrigger
      overlay={
        <Tooltip id='button-tooltip'>
          <div>On Sale Until</div>
          <div>{saleEndTime}</div>
        </Tooltip>
      }
      placement='top'
    >
      {children}
    </OverlayTrigger>
  ) : (
    children
  )

type Props = {
  card: CardType
  scalable?: boolean
}

const CardPrices = ({ card, scalable }: Props) => {
  const { tcgplayerKickback, tcgplayerSale } = useAppSelector((state) => state.misc)
  const priceSources = useAppSelector((state) => state.user.priceSources)

  const { names, prices } = card

  const scgSlug = encodeURIComponent(
    (names ? names.join(' // ') : '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace('꞉', ':'), // Ratonhnhaké꞉ton uses a "colon sign" which is NOT a colon
  ).replace(/\s/g, '%20')

  const priceLinks: Partial<Record<keyof PriceMapType, () => React.ReactNode>> = {
    cardhoarder: () => (
      <OutboundLink
        key={v4()}
        title='Buy at Cardhoarder'
        url={`https://www.cardhoarder.com/cards/${prices.cardhoarder.slug}?affiliate_id=edhrec`}
      >
        <div className={styles.price}>
          <span>{`${prices.cardhoarder.price} TIX`}</span>
        </div>
      </OutboundLink>
    ),
    cardkingdom: () => (
      <OutboundLink
        key={v4()}
        title='Buy at Card Kingdom'
        url={`https://www.cardkingdom.com/${prices.cardkingdom.slug}?partner=edhrec&utm_campaign=edhrec`}
        utm_medium='affiliate'
        utm_source='edhrec'
      >
        <div className={styles.price}>
          <Icon icon='cardkingdom' />
          <span>{`$${prices.cardkingdom.price.toFixed(2)}`}</span>
        </div>
      </OutboundLink>
    ),
    cardmarket: () => {
      const url =
        'slug' in prices.cardmarket
          ? `https://www.cardmarket.com/en/Magic/Products/Search?searchString=${(prices.cardmarket as PriceWithSlugType).slug}&utm_campaign=card_prices`
          : `${(prices.cardmarket as PriceWithUrlType).url}&utm_campaign=card_prices`
      return (
        <OutboundLink key={v4()} title='Buy at Cardmarket' url={url} utm_medium='text' utm_source='edhrec'>
          <div className={styles.price}>
            <Icon icon='cardmarket' />
            <span>
              {`${
                typeof prices.cardmarket.price === 'number'
                  ? prices.cardmarket.price.toFixed(2)
                  : prices.cardmarket.price
              } €`.replace('.', ',')}
            </span>
          </div>
        </OutboundLink>
      )
    },
    cardtrader: () => (
      <OutboundLink
        key={v4()}
        title='Buy at CardTrader'
        url={`https://www.cardtrader.com/en/cards/${prices.cardtrader.slug}?share_code=SpaceCowMedia`}
        utm_medium='text'
      >
        <div className={styles.price}>
          <Icon icon='cardtrader' />
          <span>
            {`${
              typeof prices.cardtrader.price === 'number' ? prices.cardtrader.price.toFixed(2) : prices.cardtrader.price
            } €`.replace('.', ',')}
          </span>
        </div>
      </OutboundLink>
    ),
    face2face: () => {
      const fixedName = card.names.length > 1 ? card.names.join(' // ') : card.name
      const url = `https://www.facetofacegames.com/search?q=${fixedName}&dt_id=2364253`
      return (
        <OutboundLink key={v4()} title='Buy at Face to Face Games' url={url} utm_medium='affiliate'>
          <div className={styles.price}>
            <Icon icon='face2face' />
            <span>${prices.face2face.price.toFixed(2)}</span>
          </div>
        </OutboundLink>
      )
    },
    manapool: () => (
      <OutboundLink
        key={v4()}
        title='Buy at Mana Pool'
        url={`https://manapool.com/card/${prices.manapool.slug}?ref=scm&ref_meta=referrer:edhrec,cardprice`}
        utm_medium='affiliate'
      >
        <div className={styles.price}>
          <Icon icon='manapool' />
          <span>${prices.manapool.price.toFixed(2)}</span>
        </div>
      </OutboundLink>
    ),
    scg: () => (
      <OutboundLink
        disableUtm
        key={v4()}
        title='Buy at Star City Games'
        url={impactLink({
          partner: 'scg',
          subId1: 'edhrec,cardprice',
          u: `https://starcitygames.com/search/?item_display_name=${scgSlug}`,
        })}
      >
        <div className={styles.price}>
          <Icon icon='scg' />
          <span>${prices.scg.price}</span>
        </div>
      </OutboundLink>
    ),
    tcgplayer: () => {
      const end = tcgplayerKickback ? new Date(tcgplayerKickback.end) : new Date()
      const saleEndTime = `${end.getHours() % 12}:${end.getMinutes().toString().padStart(2, '0')} ${
        end.getHours() > 11 ? 'P' : 'A'
      }M ${end.toLocaleDateString()}`
      return (
        <TCGPContainer key={v4()} saleEndTime={saleEndTime} tcgplayerSale={tcgplayerSale}>
          <a
            href={impactLink({
              partner: 'tcgplayer',
              subId1: 'edhrec,cardprice',
              u: `https://www.tcgplayer.com/product/${prices.tcgplayer.slug}`,
            })}
            rel='noopener noreferrer'
            target='_blank'
          >
            <div className={classNames(styles.price, { [styles.sale]: tcgplayerSale > 0 })}>
              <Icon icon='tcgplayer' />
              <span>{`$${(((100 - tcgplayerSale) / 100) * prices.tcgplayer.price).toFixed(2)}`}</span>
            </div>
          </a>
        </TCGPContainer>
      )
    },
  }
  return (
    <div className={classNames(styles.prices, scalable && styles.scalable)}>
      {/* priceSources should always be defined, but a user/browser could mess it up */}
      {prices && priceSources && (
        <>
          {[0, 1, 2].map((i) => {
            let priceSource = priceSources[i] as keyof PriceMapType

            // Fix users with CSI in their settings, which was removed
            if (priceSource === ('coolstuffinc' as any)) priceSource = 'scg'

            return prices[priceSource] && priceLinks[priceSource] ? (
              (priceLinks[priceSource] as any)()
            ) : (
              <span key={v4()}>&nbsp;</span>
            )
          })}
        </>
      )}
    </div>
  )
}

export default CardPrices
